import React, { memo, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import classNames from 'classnames';
import { EarthLoader } from '../../../components/loaders/EarthLoader';
import { usePotreeEvents } from '../../../hooks/potree/usePotreeEvents';
import { useRealtimeUpdates } from '../../../hooks/potree/useRealtimeUpdates';
import { useRendererShortcuts } from '../../../hooks/potree/useRendererShortcuts';
import { RendererHeader } from '../../../modules/renderer/RendererHeader';
import { RendererLeftPanel, LeftPanelTabs } from '../../../modules/renderer/RendererLeftPanel';
import { RendererRightPanel } from '../../../modules/renderer/RendererRightPanel';
import { ScreenTools, Shortcuts } from '../../../modules/renderer/ScreenTools';
import { ViewSelector } from '../../../components/ViewSelector';
import { RoomTagNameEditor } from '../../../components/RoomTagNameEditor';
import { useRoomFolderSync } from '../../../hooks/potree/useRoomFolderSync';
import { ProjectShellFragment } from '../../../types/graphqlTypes';
import { useLocalStorage } from 'usehooks-ts';
import useOpen from '../../../hooks/useOpen';
import { useDeviceSize } from '../../../hooks/useDeviceSize';
import { HintBar } from '../../../components/HintBar';
import { useHintBar } from '../../../hooks/useHintBar';
import { HeatmapLegendOverlay } from '../../../components/HeatmapLegendOverlay';
import { ToastNotifications } from '../../../components/ToastNotifications';
import { useContext as useReactContext } from 'react';
import { RendererReadOnlyContext } from '../../../contexts/RendererReadOnlyContext';
import { RendererContext } from '../../../contexts/RendererContext';
import { usePersonaManager } from '../../../hooks/usePersonaManager';
import { useQuestHintBar } from '../../../hooks/useToolTooltipState';
import { usePotreeTranslation } from '../../../hooks/potree/usePotreeTranslation';
import { PERSONAS } from '../../../config/personas';
import { ToolSettingsMenuButton } from '../../../modules/renderer/ToolSettingsMenuButton';
import { LayoutWallSketchBootstrap } from '../../../modules/renderer/LayoutWallSketchBootstrap';
import { WallSourcePrompt } from '../../../modules/renderer/WallSourcePrompt';
import { RenderViewMorphShell } from '../../../components/RenderViewWidget/MorphShell';
// Side-effect import: registers user properties for the widget at app startup.
import '../../../modules/Properties/integration/WidgetSettingsIntegration';

// Map potree background names to CSS colors
const backgroundColors: Record<string, string> = {
  black: '#121212',
  white: '#ffffff',
  dark: '#313131',
  light: '#cdcdcd',
};

/** Barrier components isolate hooks that subscribe to annotation data.
 *  Without isolation, annotation changes cascade through ProjectContent to all children. */
const PotreeEventsBarrier: React.FC<{ project?: ProjectShellFragment }> = ({ project }) => {
  usePotreeEvents({ project });
  return null;
};

const ShortcutsBarrier: React.FC = () => {
  useRendererShortcuts();
  return null;
};

/** Keeps room folders (ROOM groups) reconciled with room annotations. */
const RoomFolderSyncBarrier: React.FC = () => {
  useRoomFolderSync();
  return null;
};

const ProjectContent_: React.FC2<{ project?: ProjectShellFragment }> = ({ project }) => {
  useRealtimeUpdates();

  const { hintText } = useHintBar();

  // Persona and modes integration
  const { viewer } = useReactContext(RendererContext);

  // Sync React translations into Potree's TranslationService
  usePotreeTranslation(viewer);

  // Quest-driven hint bar: listens for tool state changes and updates the HintBar
  useQuestHintBar(viewer);

  // Track background color from the potree viewer. useSyncExternalStore is the supported way to
  // read from an external store: the snapshot covers the initial value, so there is no separate
  // "seed the state in an effect" step.
  const subscribeToBackground = useCallback(
    (onStoreChange: () => void) => {
      if (!viewer) return () => undefined;
      viewer.addEventListener('background_changed', onStoreChange);
      return () => viewer.removeEventListener('background_changed', onStoreChange);
    },
    [viewer],
  );
  const getBackgroundSnapshot = useCallback(() => {
    const bg = viewer?.getBackground?.();
    return (bg && backgroundColors[bg]) || null;
  }, [viewer]);
  const backgroundColor = useSyncExternalStore(subscribeToBackground, getBackgroundSnapshot, () => null);
  const { activePersona, currentMode, setMode, disabledModes } = usePersonaManager(viewer);
  const activePersonaConfig = PERSONAS.find((p) => activePersona?.name.toLowerCase() === p.id);
  const availableModes = activePersonaConfig?.modes || [];
  const isReadOnly = useReactContext(RendererReadOnlyContext);

  // Read-only viewers have no mode tabs: keep each persona on its end-result mode (the floorplan
  // persona opens on floor selection, which is an editing view).
  const readOnlyModeId = activePersonaConfig?.readOnlyModeId;
  useEffect(() => {
    if (!isReadOnly || !readOnlyModeId || !currentMode || currentMode.id === readOnlyModeId) return;
    setMode(readOnlyModeId);
  }, [isReadOnly, readOnlyModeId, currentMode, setMode]);
  const hasToolsBar = true; // ToolsBar background bar is still visible

  useEffect(() => {
    const onTouchEnd = (e: TouchEvent) => {
      e.target?.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          clientX: e.changedTouches[0].clientX,
          clientY: e.changedTouches[0].clientY,
        }),
      );
      e.preventDefault();
    };
    // replace default touchEnd behavior with mouse click. (bugfix: see PK-254)
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  // Destructure up front so the memo closes over individual fields rather than the whole
  // `project` object — otherwise the header rebuilds on every unrelated project update.
  const projectId_ = project?.id;
  const projectName = project?.name ?? '';
  const projectDescription = project?.description;
  const projectPreviewUrl = project?.previewUrl;
  const projectSettings = project?.settings;
  const projectPointClouds = project?.pointClouds;

  const rendererHeaderProps = useMemo(
    () =>
      projectId_ && {
        project: {
          id: projectId_,
          name: projectName,
          description: projectDescription,
          previewUrl: projectPreviewUrl,
          settings: projectSettings,
          pointClouds: (projectPointClouds ?? []).map((pc) => ({
            id: pc.id,
            displayName: pc.displayName,
            availableClasses: pc.availableClasses,
          })),
        },
      },
    [projectId_, projectName, projectDescription, projectPreviewUrl, projectSettings, projectPointClouds],
  );

  const allPanelsTogether = false; // email === 'jonathan@softvalla.com';

  const { mdDevice } = useDeviceSize();

  const [leftPanelOpenStorage, setLeftPanelOpenStorage] = useLocalStorage('left-panel-open', !mdDevice);
  const { onClose: closeLeftPanel, onOpen: openLeftPanel, open: leftPanelOpen } = useOpen(leftPanelOpenStorage);

  useEffect(() => {
    setLeftPanelOpenStorage(leftPanelOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftPanelOpen]);

  const [rightPanelOpenStorage, setRightPanelOpenStorage] = useLocalStorage('right-panel-open', !mdDevice);
  const { onClose: closeRightPanel, onOpen: openRightPanel, open: rightPanelOpen } = useOpen(rightPanelOpenStorage);

  const [leftPanelTab, setLeftPanelTab] = useState<LeftPanelTabs>(LeftPanelTabs.OBJECTS);

  useEffect(() => {
    setRightPanelOpenStorage(rightPanelOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightPanelOpen]);

  const onOpenLeftPanel = useCallback(() => {
    if (leftPanelOpen) return;
    openLeftPanel();
    if (mdDevice) closeRightPanel();
  }, [closeRightPanel, leftPanelOpen, mdDevice, openLeftPanel]);

  const onOpenRightPanel = useCallback(() => {
    if (rightPanelOpen) return;
    openRightPanel();
    if (mdDevice) closeLeftPanel();
  }, [closeLeftPanel, mdDevice, openRightPanel, rightPanelOpen]);

  useEffect(() => {
    if (!mdDevice) return;
    if (leftPanelOpen && rightPanelOpen) {
      closeRightPanel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mdDevice]);

  const hideLeftArrow = mdDevice && rightPanelOpenStorage;
  const hideRightArrow = mdDevice && leftPanelOpenStorage;

  return (
    <div className="flex flex-col w-full h-full bg-white dark:bg-[#313131] dark:text-white">
      <PotreeEventsBarrier project={project} />
      <ShortcutsBarrier />
      <RoomFolderSyncBarrier />
      {<EarthLoader />}
      {rendererHeaderProps && <RendererHeader {...rendererHeaderProps} minimize={mdDevice} modes={availableModes} currentModeId={currentMode?.id} onModeChange={setMode} disabledModes={disabledModes} leftPanelTab={leftPanelTab} />}
      <div className="flex w-full h-full overflow-auto">
        {!allPanelsTogether && <RendererLeftPanel project={project} closePanel={closeLeftPanel} openPanel={onOpenLeftPanel} panelOpen={leftPanelOpen} hideArrow={hideLeftArrow} selectedTab={leftPanelTab} onTabChange={setLeftPanelTab} />}
        <div className="relative w-full h-full">
          <div className="absolute top-0 left-0 w-full h-full potree_container">
            <div id="potree_render_area">
              <div id="olContainer" className="absolute top-0 left-0 w-full h-full bg-[#cdcdcd] dark:bg-[#313131]" style={backgroundColor ? { backgroundColor } : undefined} />
            </div>
          </div>
          {/* Background bar for tools - tools themselves are in the tabs */}
          <div data-panel="tools-bar" className="absolute top-0 left-0 right-0 z-[5] h-[52px] bg-[rgb(210,210,210)] dark:bg-[rgb(45,45,45)] flex items-center justify-end pr-4">
            <ToolSettingsMenuButton />
          </div>
          <HintBar text={hintText} className={classNames('absolute left-0 right-0 z-[4]', hasToolsBar ? 'top-[60px]' : 'top-2')} />
          <HeatmapLegendOverlay />
          <ToastNotifications className={classNames('absolute right-4 z-30', hasToolsBar ? 'top-20' : 'top-4')} />
          <div className="absolute bottom-4 left-4 flex flex-col items-start gap-2">
            <ViewSelector />
          </div>
          <ScreenTools className="absolute transform -translate-x-1/2 bottom-4 left-1/2" project={project} />
          <RoomTagNameEditor />
          <Shortcuts className="absolute bottom-4 right-4" project={project} />
          {/* Floating second-view widget. Morphs from a top-left toggle
              button into the panel at the user's saved layout. Clamps to
              this `relative` wrapper (same parent as #potree_render_area). */}
          <RenderViewMorphShell />
        </div>
        {!allPanelsTogether ? (
          <RendererRightPanel project={project} closePanel={closeRightPanel} openPanel={onOpenRightPanel} panelOpen={rightPanelOpen} hideArrow={hideRightArrow} />
        ) : (
          <div className="flex flex-col flex-shrink-0 h-full w-96">
            <div className="overflow-auto h-1/2">
              <RendererLeftPanel project={project} closePanel={closeLeftPanel} openPanel={onOpenLeftPanel} panelOpen={leftPanelOpen} hideArrow={hideLeftArrow} selectedTab={leftPanelTab} onTabChange={setLeftPanelTab} />
            </div>
            <div className="overflow-auto h-1/2">
              <RendererRightPanel project={project} closePanel={closeRightPanel} openPanel={onOpenRightPanel} panelOpen={rightPanelOpen} hideArrow={hideRightArrow} />
            </div>
          </div>
        )}
      </div>
      <LayoutWallSketchBootstrap />
      <WallSourcePrompt />
    </div>
  );
};
export const ProjectContent = memo(ProjectContent_);
